import { Coins, Loader2, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { setCreditTopups } from '@/lib/collections/creditTopups';
import { subscribeUserCredits, type UserCreditsResponse } from '@/lib/collections/userCredits';
import { ADMIN_CREATOR_ADDRESS, NEW_WALLET_CREDITS, TOPUP_CREDITS, TOPUP_LAMPORTS } from '@/lib/constants';
import { Address, Time } from '@/lib/db-client';
import { formatSol } from '@/utils/format';

export const NEW_WALLET_CREDITS_NUM = Number(NEW_WALLET_CREDITS);
export const TOPUP_CREDITS_NUM = Number(TOPUP_CREDITS);
export const TOPUP_LAMPORTS_NUM = Number(TOPUP_LAMPORTS);

/**
 * Reads a wallet's credit balance in real time. Admins are unlimited and
 * never need a subscription. A missing userCredits record means the wallet
 * hasn't spent/topped-up yet, so it's treated as NEW_WALLET_CREDITS (50).
 */
export function useCreditBalance(address: string | undefined | null) {
  const isAdmin = !!address && address === ADMIN_CREATOR_ADDRESS;
  const { data, loading } = useRealtimeData<UserCreditsResponse | null>(
    subscribeUserCredits,
    !!address && !isAdmin,
    address ?? ''
  );
  const balance = data?.balance ?? NEW_WALLET_CREDITS_NUM;
  return { balance, loading: isAdmin ? false : loading, isAdmin };
}

interface BuyCreditsDialogProps {
  walletAddress: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Solana-Pay style confirm dialog that tops up credits via an onchain SOL transfer. */
export const BuyCreditsDialog: React.FC<BuyCreditsDialogProps> = ({ walletAddress, open, onOpenChange }) => {
  const [buying, setBuying] = useState(false);

  const handleBuy = async () => {
    setBuying(true);
    try {
      const topupId = crypto.randomUUID();
      const success = await setCreditTopups(topupId, {
        buyer: Address.publicKey(walletAddress),
        lamportsPaid: TOPUP_LAMPORTS_NUM,
        creditsAdded: TOPUP_CREDITS_NUM,
        timestamp: Time.Now,
      });
      if (!success) {
        toast.error('Payment was rejected or failed. Please try again.');
        return;
      }
      toast.success(`+${TOPUP_CREDITS_NUM} credits added to your balance!`);
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Could not complete purchase.');
    } finally {
      setBuying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" /> Buy Credits
          </DialogTitle>
          <DialogDescription>
            Top up your credit balance to create more agents. Paid in test SOL on Poofnet — no real funds involved.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl border border-border bg-card p-4 my-1 text-center">
          <p className="text-2xl font-extrabold tracking-tight">
            {formatSol(TOPUP_LAMPORTS_NUM)} → +{TOPUP_CREDITS_NUM} credits
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            One onchain SOL transfer to the treasury, credited instantly to your balance.
          </p>
        </div>
        <button
          onClick={handleBuy}
          disabled={buying}
          className="w-full rounded-full bg-[#00C805] text-white font-bold py-3 shadow-lg shadow-[#00C805]/25 hover:bg-[#00b004] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {buying && <Loader2 className="w-4 h-4 animate-spin" />}
          {buying ? 'Processing…' : `Confirm — ${formatSol(TOPUP_LAMPORTS_NUM)}`}
        </button>
      </DialogContent>
    </Dialog>
  );
};

interface CreditBalancePillProps {
  walletAddress: string;
  className?: string;
}

/** Compact balance chip with a "buy" affordance — used in the top nav. */
export const CreditBalancePill: React.FC<CreditBalancePillProps> = ({ walletAddress, className }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { balance, loading, isAdmin } = useCreditBalance(walletAddress);

  return (
    <>
      <div
        className={`flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-2.5 py-1.5 text-xs font-bold shrink-0 ${className ?? ''}`}
      >
        <Coins className="w-3.5 h-3.5 text-primary" />
        <span className="tabular-nums">{isAdmin ? 'Unlimited' : loading ? '—' : balance}</span>
        {!isAdmin && (
          <button
            onClick={() => setDialogOpen(true)}
            aria-label="Buy credits"
            className="ml-0.5 rounded-full bg-primary/15 hover:bg-primary/25 p-0.5 transition-colors"
          >
            <Plus className="w-3 h-3 text-primary" />
          </button>
        )}
      </div>
      {!isAdmin && (
        <BuyCreditsDialog walletAddress={walletAddress} open={dialogOpen} onOpenChange={setDialogOpen} />
      )}
    </>
  );
};

export default CreditBalancePill;
